import React, { createContext, useContext, useEffect, useState } from 'react';
import { LocationResult } from '../../../../types/SchedulerTypes';
import { devLog, devError } from '@src/utils/environment';
interface FacilityContextValue {
  facility: LocationResult | null;
  loading: boolean;
  error: string | null;
  // Helper methods for office details display
  getOfficeName: () => string;
  getOfficeAddress: () => string;
  getOfficeDistance: () => string;
}
interface FacilityContextProviderProps {
  siteId: string;
  children: React.ReactNode;
  appName?: string;
  appVersion?: string;
  brand?: string;
  user?: any;
}
const FacilityContext = createContext<FacilityContextValue | undefined>(undefined);
export const FacilityContextProvider: React.FC<FacilityContextProviderProps> = ({
  siteId,
  children,
  ...rest
}) => {
  const [facility, setFacility] = useState<LocationResult | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!siteId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`/schedule-appointment/api/details/${siteId}/`).then(res => {
      if (!res.ok) throw new Error('Failed to fetch facility details');
      return res.json();
    }).then(data => {
      devLog('FacilityContextProvider: Facility details loaded successfully');
      devLog({
        facilityData: data
      });
      setFacility(data[0] || null);
      setLoading(false);
    }).catch(err => {
      devError('FacilityContextProvider: Error fetching facility details:', err);
      setError(err.message);
      setLoading(false);
    });
  }, [siteId]);

  // Helper methods for office details display
  const getOfficeName = () => {
    return facility?.displayName || facility?.siteName || '';
  };
  const getOfficeAddress = () => {
    if (!facility) return '';
    return `${facility.address}, ${facility.city}, ${facility.state} ${facility.zip}`;
  };
  const getOfficeDistance = () => {
    return facility?.distanceInMiles?.toString() || '';
  };
  return <FacilityContext.Provider value={{
    facility,
    loading,
    error,
    getOfficeName,
    getOfficeAddress,
    getOfficeDistance
  }} data-sentry-element="FacilityContext.Provider" data-sentry-component="FacilityContextProvider" data-sentry-source-file="FacilityContextProvider.tsx">
			{children}
		</FacilityContext.Provider>;
};
export const useFacilityContext = () => {
  const context = useContext(FacilityContext);
  if (!context) {
    throw new Error('useFacilityContext must be used within a FacilityContextProvider');
  }
  return context;
};